/**
 * @format
 */

import { useQuery } from '@apollo/client';
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  GET_LEADERBOARD,
  GET_WEEKLY_LEADERBOARD,
  AgentLeaderboardItem,
  WeeklyLeaderboardItem,
  UserWeeklySalesItem,
  AgentLeaderboardFilterInput,
  WeeklyLeaderboardFilterInput,
} from '../services/leaderboardService';

interface UseLeaderboardReturn {
  data: AgentLeaderboardItem[];
  loading: boolean;
  error: any;
  refetch: () => void;
}

interface UseWeeklyLeaderboardReturn {
  data: WeeklyLeaderboardItem[][];
  loading: boolean;
  error: any;
  refetch: () => void;
}

interface UseUserWeeklySalesReturn {
  data: UserWeeklySalesItem[];
  loading: boolean;
  error: any;
  refetch: () => void;
}

const API_URL = process.env.REACT_APP_SIADASH_BACKEND_URL;

/**
 * Hook to fetch agent leaderboard using GraphQL
 */
export function useLeaderboard(
  filter?: AgentLeaderboardFilterInput
): UseLeaderboardReturn {
  const { data, loading, error, refetch } = useQuery<{
    getLeaderboard: AgentLeaderboardItem[];
  }>(GET_LEADERBOARD, {
    variables: { filter: filter || {} },
    skip: false,
  });

  console.log('useLeaderboard filter:', filter);
  console.log('useLeaderboard data:', data);

  return {
    data: data?.getLeaderboard || [],
    loading,
    error,
    refetch,
  };
}

/**
 * Hook to fetch weekly leaderboard using GraphQL
 */
export function useWeeklyLeaderboard(
  filter?: WeeklyLeaderboardFilterInput,
  pollInterval?: number
): UseWeeklyLeaderboardReturn {
  const { data, loading, error, refetch } = useQuery<{
    getWeeklyLeaderboard: WeeklyLeaderboardItem[][];
  }>(GET_WEEKLY_LEADERBOARD, {
    variables: { filter },
    pollInterval, // Auto-refetch at this interval (in milliseconds)
  });

  return {
    data: data?.getWeeklyLeaderboard || [],
    loading,
    error,
    refetch,
  };
}

/**
 * Hook to fetch user weekly sales using REST API
 */
export function useUserWeeklySales(
  userId?: string
): UseUserWeeklySalesReturn {
  const [data, setData] = useState<UserWeeklySalesItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<any>(null);

  const fetchData = useCallback(async () => {
    if (!userId) {
      setData([]);
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get<UserWeeklySalesItem[]>(
        `${API_URL}/api/dashboard/user-weekly-sales`,
        {
          params: { userId },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setData(response.data || []);
      setError(null);
    } catch (err: any) {
      console.error('Error fetching user weekly sales:', err);
      setError(err);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    loading,
    error,
    refetch: fetchData,
  };
}

